import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { titleCase } from '../../components/format';
import type { JobDTO, JobInput } from '@em/shared';
import { api, qk, queryClient, useJobs, useMyobStatus, useSites } from '../../api';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, EmptyState, ErrorAlert, Field, Input, QueryState, Select } from '../../components/ui';

export function JobsSettings() {
  const q = useJobs();
  const sites = useSites();
  const [editing, setEditing] = useState<(JobInput & { id?: string }) | null>(null);
  return (
    <Card
      title="Jobs"
      tight
      actions={
        <Button size="sm" variant="primary" onClick={() => setEditing({ code: '', name: '', status: 'active' })}>
          Add job
        </Button>
      }
    >
      <QueryState q={q}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState title="No jobs yet">Jobs let staff record which job they are working on, for job costing.</EmptyState>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Customer</th>
                  <th>Site</th>
                  <th className="num">Budget hours</th>
                  <th>MYOB job</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((j: JobDTO) => (
                  <tr key={j.id}>
                    <td className="mono strong">{j.code}</td>
                    <td>{j.name}</td>
                    <td>{j.customer ?? '–'}</td>
                    <td>{sites.data?.find((s) => s.id === j.siteId)?.name ?? '–'}</td>
                    <td className="num">{j.budgetHours ?? '–'}</td>
                    <td>{j.myobJobUid ? <Badge tone="green">Linked</Badge> : <Badge tone="amber">Not linked</Badge>}</td>
                    <td>
                      <Badge tone={j.status === 'active' ? 'green' : j.status === 'on_hold' ? 'amber' : 'grey'}>{titleCase(j.status)}</Badge>
                    </td>
                    <td className="actions">
                      <Button size="sm" onClick={() => setEditing({ ...j })}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </QueryState>
      {editing && <JobDialog initial={editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function JobDialog({ initial, onClose }: { initial: JobInput & { id?: string }; onClose: () => void }) {
  const sites = useSites();
  const myob = useMyobStatus();
  const connected = !!myob.data?.connected && !!myob.data.companyFileUri;
  const myobJobs = useQuery({ queryKey: qk.myobJobs, queryFn: api.myobJobs, enabled: connected, retry: false });
  const toast = useToast();
  const [f, setF] = useState(initial);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof JobInput>(k: K, v: JobInput[K]) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const { id, ...b } = f;
      const body: JobInput = {
        code: b.code.trim(),
        name: b.name.trim(),
        customer: b.customer?.trim() || null,
        siteId: b.siteId || null,
        status: b.status,
        budgetHours: b.budgetHours ?? null,
        myobJobUid: b.myobJobUid?.trim() || null,
      };
      if (id) await api.updateJob(id, body);
      else await api.createJob(body);
      await queryClient.invalidateQueries({ queryKey: qk.jobs });
      toast.success('Job saved');
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={f.id ? `Edit job ${initial.code}` : 'Add job'}
      onClose={onClose}
      onSubmit={save}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!f.code.trim() || !f.name.trim()}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />
        <div className="form-grid">
          <Field label="Code" required>
            <Input value={f.code} onChange={(e) => set('code', e.target.value)} maxLength={32} required />
          </Field>
          <Field label="Status">
            <Select value={f.status ?? 'active'} onChange={(e) => set('status', e.target.value as JobInput['status'])}>
              <option value="active">Active</option>
              <option value="on_hold">On hold</option>
              <option value="complete">Complete</option>
            </Select>
          </Field>
          <Field label="Name" required className="full">
            <Input value={f.name} onChange={(e) => set('name', e.target.value)} required />
          </Field>
          <Field label="Customer">
            <Input value={f.customer ?? ''} onChange={(e) => set('customer', e.target.value)} />
          </Field>
          <Field label="Site">
            <Select value={f.siteId ?? ''} onChange={(e) => set('siteId', e.target.value || null)}>
              <option value="">No site</option>
              {sites.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Budget hours">
            <Input type="number" min={0} step="any" value={f.budgetHours ?? ''} onChange={(e) => set('budgetHours', e.target.value === '' ? null : Number(e.target.value))} />
          </Field>
          <Field label="MYOB job">
            {myobJobs.data ? (
              <Select value={f.myobJobUid ?? ''} onChange={(e) => set('myobJobUid', e.target.value || null)}>
                <option value="">Not linked</option>
                {myobJobs.data.map((j) => (
                  <option key={j.uid} value={j.uid}>
                    {j.number} {j.name}
                  </option>
                ))}
              </Select>
            ) : (
              <Input className="mono" placeholder="MYOB job UID" value={f.myobJobUid ?? ''} onChange={(e) => set('myobJobUid', e.target.value)} />
            )}
          </Field>
        </div>
      </div>
    </Modal>
  );
}
